import { graphql, PageProps } from "gatsby"
import React from "react"
import type { HeadFC } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import { AuthorContextProvider } from "@talentinc/gatsby-theme-ecom/contexts/AuthorContext"
import { AuthorPageData } from "@talentinc/gatsby-theme-ecom/types/blog/author"
import { PageContextType } from "@talentinc/gatsby-theme-ecom/types/page"
import AuthorPage from "@talentinc/gatsby-theme-ecom/pages/blog/AuthorPage"
import pageData from "../../data/career-advice/author-page"
import useUrlPathBuilder from "@talentinc/gatsby-theme-ecom/hooks/useUrlPathBuilder"
import insertHandlebarsTemplate from "@talentinc/gatsby-theme-ecom/utils/insertHandlebarsTemplate"
import useHandlebarsContext from "@talentinc/gatsby-theme-ecom/hooks/useHandlebarsContext"
import { brand } from "../../data/common/brand"

export default function Page({
  pageContext,
  data,
}: PageProps<AuthorPageData, PageContextType & { numPages: number; currentPage: number; slug: string }>) {
  const { numPages, currentPage, slug } = pageContext

  const { authorBySlug } = data

  const updatedPageData = {
    ...pageData,
    title: authorBySlug?.authorName,
    slug: `career-advice/author/${authorBySlug?.slug}${currentPage !== 1 ? "/" + currentPage : ""}`,
    seo: {
      ...pageData.seo,
      pageTitle: authorBySlug?.metaTitle || null,
      description: authorBySlug?.metaDescription || null,
      noIndex: !authorBySlug?.indexStatus,
      canonicalUrl: authorBySlug?.canonicalUrl || null,
    },
  }

  return (
    <AuthorContextProvider
      value={{
        numPages,
        currentPage,
        slug,
        articleByAuthor: data.articleByAuthor.nodes,
        authorBySlug: data.authorBySlug,
      }}
    >
      <AuthorPage pageContext={pageContext} pageData={updatedPageData} />
    </AuthorContextProvider>
  )
}

export const Head: HeadFC<
  AuthorPageData,
  PageContextType & {
    numPages: number
    currentPage: number
    slug: string
  }
> = ({ data, pageContext }) => {
  const { authorBySlug } = data
  const { currentPage, numPages, slug = [] } = pageContext

  const { urlPathBuilder } = useUrlPathBuilder() // Remove baseURL from destructuring
  const handlebarsContext = useHandlebarsContext()
  const {
    i18n: { language },
  } = useTranslation()

  // Get the language with proper fallback
  const getLanguage = () => {
    if (language && language !== "en") {
      return language
    }
    return "en-GB" // Default fallback for TopCV.co.uk
  }

  const pageLanguage = getLanguage()

  // Use brand's productionUrl directly like react-helmet does
  const absoluteBaseURL = brand.brand.productionUrl

  // slug logic
  const slugToUse = `career-advice/author/${authorBySlug?.slug}`

  // canonical URL - include page number for paginated pages
  const canonicalUrl =
    authorBySlug?.canonicalUrl ??
    (currentPage === 1
      ? urlPathBuilder([slugToUse], { baseURL: absoluteBaseURL })
      : urlPathBuilder([`career-advice/author/${slug}/${currentPage}`], { baseURL: absoluteBaseURL }))

  // title & description with Handlebars templating
  const pageTitle = insertHandlebarsTemplate(
    authorBySlug?.metaTitle ?? `${authorBySlug?.authorName} - TopCV Author` ?? "Author",
    handlebarsContext
  )
  const description = insertHandlebarsTemplate(
    authorBySlug?.metaDescription ?? `Articles written by ${authorBySlug?.authorName}` ?? "Author description",
    handlebarsContext
  )

  // noindex flag
  const noIndex = !authorBySlug?.indexStatus

  // Pagination links - matching AuthorPage component logic
  const prevUrl =
    currentPage !== 1
      ? urlPathBuilder([`career-advice/author/${slug}${currentPage === 2 ? "" : `/${currentPage - 1}`}`], {
          baseURL: absoluteBaseURL,
        })
      : null
  const nextUrl =
    currentPage !== numPages
      ? urlPathBuilder([`career-advice/author/${slug}/${currentPage + 1}`], {
          baseURL: absoluteBaseURL,
        })
      : null

  // Open Graph image - use author image if available
  let openGraphImage = authorBySlug?.authorImage?.file?.url

  // Format image URL - add https:// if it starts with //
  if (openGraphImage && openGraphImage.startsWith("//")) {
    openGraphImage = `https:${openGraphImage}`
  }

  // Twitter image alt text
  const twitterImageAlt = authorBySlug?.authorName

  // JSON-LD: Organization (matching react-helmet SEO/index.tsx)
  const organizationLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": urlPathBuilder([], {
      baseURL: absoluteBaseURL,
      hash: "organization",
    }),
    name: brand.brand.name,
    url: brand.brand.productionUrl,
    logo: `https:${brand.brand.logo?.file?.url}`,
    telephone: brand.brand.phoneNumber,
    parentOrganization: brand.brand.parentOrganization,
    sameAs: [
      brand.brand.twitter && `https://twitter.com/${brand.brand.twitter}`,
      brand.brand.linkedIn,
      brand.brand.youTube,
      brand.brand.facebook,
      brand.brand.instagram,
    ].filter((url): url is string => Boolean(url)),
  }

  // JSON-LD: Website (matching react-helmet SEO/index.tsx)
  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": urlPathBuilder([], {
      baseURL: absoluteBaseURL,
      hash: "website",
    }),
    url: urlPathBuilder([], { baseURL: absoluteBaseURL }),
    name: brand.brand.name,
    description: brand.brand.name,
    publisher: {
      "@id": urlPathBuilder([], {
        baseURL: absoluteBaseURL,
        hash: "organization",
      }),
    },
    potentialAction: {
      "@type": "SearchAction",
      target:
        urlPathBuilder([brand.brand.blogPrefix, "search"], { baseURL: absoluteBaseURL }) + `?s={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
    inLanguage: pageLanguage,
  }

  // JSON-LD: WebPage
  const webPageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": urlPathBuilder([slugToUse], {
      baseURL: absoluteBaseURL,
      hash: "webpage",
    }),
    name: pageTitle,
    description,
    url:
      currentPage === 1
        ? urlPathBuilder([slugToUse], { baseURL: absoluteBaseURL })
        : urlPathBuilder([`career-advice/author/${slug}/${currentPage}`], { baseURL: absoluteBaseURL }),
    isPartOf: {
      "@id": urlPathBuilder([], {
        baseURL: absoluteBaseURL,
        hash: "website",
      }),
    },
    about: {
      "@id": urlPathBuilder([], {
        baseURL: absoluteBaseURL,
        hash: "organization",
      }),
    },
    potentialAction: [
      {
        "@type": "ReadAction",
        target: [canonicalUrl],
      },
    ],
    inLanguage: pageLanguage,
  }

  // JSON-LD: BreadcrumbList
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "WebPage",
          "@id": urlPathBuilder([], { baseURL: absoluteBaseURL }),
          url: urlPathBuilder([], { baseURL: absoluteBaseURL }),
          name: "Home Page",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "WebPage",
          "@id": urlPathBuilder([slugToUse], { baseURL: absoluteBaseURL }),
          url: urlPathBuilder([slugToUse], { baseURL: absoluteBaseURL }),
          name: pageTitle,
        },
      },
    ],
  }

  return (
    <>
      {/* Standard SEO tags */}
      <title>
        {pageTitle} | {brand.brand.name}
      </title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Pagination links - matching AuthorPage component */}
      {prevUrl && <link rel="prev" href={prevUrl} />}
      {nextUrl && <link rel="next" href={nextUrl} />}

      {/* RSS Feed */}
      {brand.brand.rssFeedTitle && brand.brand.rssFeedHref && (
        <link
          rel="alternate"
          type="application/rss+xml"
          title={brand.brand.rssFeedTitle}
          href={brand.brand.rssFeedHref}
        />
      )}

      {/* Favicon and Apple Touch Icons - matching react-helmet */}
      {brand.brand.favicon?.file?.url && <link rel="icon" href={brand.brand.favicon.file.url} />}
      {brand.brand.favicon?.file?.url && <link rel="shortcut icon" href={brand.brand.favicon.file.url} />}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      {brand.brand.name && (
        <link
          rel="apple-touch-icon"
          href={`${
            brand.brand.productionUrl
          }/images/universal/meta/logo-${brand.brand.name.toLowerCase()}-square_inverse.png`}
        />
      )}

      {/* JSON-LD scripts */}
      <script type="application/ld+json">{JSON.stringify(webPageLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>

      {/* Open Graph */}
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={pageTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="TopCV" />
      <meta property="og:locale" content={pageLanguage} />
      {openGraphImage && <meta property="og:image" content={openGraphImage} />}
      <meta property="article:publisher" content={brand.brand.facebook} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {description && <meta name="msapplication-tooltip" content={description} />}
      {openGraphImage && <meta name="twitter:image" content={openGraphImage} />}
      {twitterImageAlt && <meta name="twitter:image:alt" content={twitterImageAlt} />}
      {brand.brand.twitter && (
        <>
          <meta name="twitter:creator" content={`@${brand.brand.twitter}`} />
          <meta name="twitter:site" content={`@${brand.brand.twitter}`} />
        </>
      )}

      {/* Twitter Data Labels - matching react-helmet TwitterSEO */}
      <meta name="twitter:label1" content="Written by" />
      <meta name="twitter:data1" content={`${brand.brand.name} Team`} />

      {/* NoIndex */}
      {noIndex && <meta name="robots" content="noindex" />}
    </>
  )
}

export const query = graphql`
  query ($language: String, $slug: String, $limit: Int, $skip: Int, $node_locale: String, $brandName: String) {
    authorBySlug: contentfulBlogAuthor(slug: { eq: $slug }, domain: { eq: $brandName }) {
      authorName
      slug
      authorDescription {
        raw
      }
      authorImage {
        file {
          url
        }
      }
      metaTitle
      metaDescription
      indexStatus
      canonicalUrl
    }
    articleByAuthor: allContentfulBlogArticle(
      limit: $limit
      skip: $skip
      filter: { author: { slug: { eq: $slug } }, node_locale: { eq: $node_locale }, domain: { eq: $brandName } }
      sort: { fields: publishedDate, order: ASC }
    ) {
      nodes {
        id
        title
        slug
        heroImage {
          file {
            url
          }
          title
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
